<template>
	<core-layout-content medium>
		<core-layout-headline-and-actions>
			<template slot="headline">{{ $t("update_user") }}</template>
		</core-layout-headline-and-actions>
		<!-- Loading -->
		<j-spinner v-if="isLoading" />

		<div v-else>
			<!-- Role -->
			<div class="fs4 lh125 fw7 c1 mb1">{{ $t("permission_group") }}</div>
			<j-card class="xs-12 bg5 r2 mb6">
				<j-list :list="computedPermissionGroup">
					<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
						<div class="flex xs-6 xs-left xs-middle fw7 c6 lh125">{{ item.label }}</div>
						<div class="flex xs-4 xs-left xs-middle lh125">{{ item.value }}</div>
						<!-- Actions -->
						<div class="flex xs-2 xs-right xs-middle">
							<crm-icon
								name="edit"
								color="c2"
								class="cu2"
								v-if="item.hasUpdate"
								@click.native="$modal.show('updatePermissionGroup')"
							/>
						</div>
					</div>
				</j-list>
			</j-card>

			<!-- Associated Users -->
			<div class="fs4 lh125 fw7 c1 mb1">{{ $t("associated_entity") }}</div>
			<j-card class="xs-12 bg5 r2">
				<j-list :list="computedAssociatedUser">
					<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
						<div class="flex xs-6 xs-left xs-middle fw7 c6 lh125">{{ item.label }}</div>

						<div class="flex xs-4 xs-left xs-middle lh125">
							<span
								v-if="item.label === $t('name')"
								class="c2 fw7 cu2"
								@click="decideLink"
							>{{ item.value }}</span>
							<span v-else>{{ item.value }}</span>
						</div>
					</div>
				</j-list>
			</j-card>

			<!-- Delete -->
			<div class="flex xs-12 xs-right bt1 bc7 pt2 mt8">
				<j-button
					type="error"
					v-can="'writeLogins'"
					@click="$modal.show('j-dialog')"
					:isLoading="isDeleteLoading"
				>{{ $t("delete_user") }}</j-button>
			</div>
			<j-dialog
				:title="$t('delete_user')"
				type="delete"
				@confirm="deleteUser"
			>{{ $t("errors.are_you_sure_you_want_to_delete_user") }}</j-dialog>
		</div>

		<!-- Update Modal -->
		<core-modal name="updatePermissionGroup" :headline="$t('update')">
			<div class="p4">
				<!-- Select Role -->
				<core-form-input
					class="mb2"
					componentName="core-select-role"
					name="role"
					v-validate="'required'"
					:label="$t('select_role')"
					:validationErrors="errors"
					v-model="selectedPermissionGroup"
				/>
				<div class="flex xs-right">
					<j-button
						v-can="'writeLogins'"
						v-if="selectedPermissionGroup"
						:isLoading="isSaveLoading"
						@click="updatePermissionGroup"
					>{{ $t("save") }}</j-button>
				</div>
			</div>
		</core-modal>
	</core-layout-content>
</template>

<script>
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import JCard from "@/components/JCard/"
import JButton from "@/components/JButton/"
import JDialog from "@/components/JDialog"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent.vue"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions.vue"
import JList from "@/components/JList/"
import CrmIcon from "@/components/CrmIcon"
import CoreModal from "@/modules/core/components/CoreModal"
import JSpinner from "@/components/JSpinner/"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import dayjs from "dayjs"

export default {
	components: {
		CoreFormInput,
		JCard,
		JButton,
		JDialog,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		JList,
		CrmIcon,
		CoreModal,
		JSpinner
	},

	data() {
		return {
			isLoading: true,
			isSaveLoading: false,
			isDeleteLoading: false,
			associatedUser: null,
			permissionGroups: [], // permission groups that user/login belongs to or has
			selectedPermissionGroup: null
		}
	},

	methods: {
		async fetchUser() {
			const query = gql`
				query fetchUser($ids: [String]) {
					logins(ids: $ids) {
						list {
							associatedUser {
								id
								__typename
								name
								createdAt
								... on internal {
									englishFirstName
									englishLastName
									chineseFirstName
									chineseLastName
								}
								... on individual {
									englishFirstName
									englishLastName
									chineseFirstName
									chineseLastName
								}
							}
							permissionGroups {
								name
								id
							}
						}
					}
				}
			`

			const variables = {
				ids: [this.loginId]
			}

			this.isLoading = true
			const res = await api({ query, variables })
			this.isLoading = false
			this.associatedUser = res?.data?.logins?.list?.[0]?.associatedUser
			this.permissionGroups = res?.data?.logins?.list?.[0]?.permissionGroups
			this.selectedPermissionGroup = this.permissionGroups?.[0]?.id
		},

		async updatePermissionGroup() {
			this.isSaveLoading = true
			const isRemoved = await this.removePermissionGroup()
			if (!isRemoved) return
			await this.addPermisionGroup()
			this.isSaveLoading = false
			this.$modal.hide("updatePermissionGroup")
			this.fetchUser()
		},

		async addPermisionGroup() {
			const query = gql`
				mutation addToPermissionGroup(
					$loginId: String!
					$permissionGroupId: String!
				) {
					addToPermissionGroup(
						loginId: $loginId
						permissionGroupId: $permissionGroupId
					) {
						status
						errors
						errors_2 {
							code
							message
						}
					}
				}
			`

			const variables = {
				loginId: this.loginId,
				permissionGroupId: this.selectedPermissionGroup
			}

			const res = await api({ query, variables })

			if (res?.data?.addToPermissionGroup?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.addToPermissionGroup?.errors ||
							res?.data?.addToPermissionGroup?.errors_2?.message ||
							"Failed to add permission group, please try again"
					}
				})

				return false
			}

			return true
		},

		async removePermissionGroup() {
			// when login has no permission group, we don't need to remove anything
			if (this?.permissionGroups?.length === 0) return true

			const query = gql`
				mutation removeFromPermissionGroup(
					$loginId: String!
					$permissionGroupId: String!
				) {
					removeFromPermissionGroup(
						loginId: $loginId
						permissionGroupId: $permissionGroupId
					) {
						errors
						status
					}
				}
			`

			const variables = {
				loginId: this.loginId,
				permissionGroupId: this.permissionGroups?.[0]?.id
			}

			const res = await api({ query, variables })

			if (res?.data?.removeFromPermissionGroup?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.removeFromPermissionGroup?.errors ||
							res?.data?.removeFromPermissionGroup?.errors_2?.message ||
							"Failed to remove permission group, please try again"
					}
				})

				return false
			}

			return true
		},

		decideLink() {
			const type = this.associatedUser?.__typename
			const id = this?.associatedUser?.id
			if (type === "internal")
				this.$router.push({ name: "InternalsDetail", params: { id } })
			if (type === "individual" || type === "company")
				this.$router.push({
					name: "ClientDetail",
					params: { id: id, clientType: type }
				})
		},

		async deleteUser() {
			const query = gql`
				mutation deleteLogin($loginId: String!) {
					deleteLogin(loginId: $loginId) {
						status
						errors
					}
				}
			`

			const variables = {
				loginId: this.loginId
			}

			this.isDeleteLoading = true
			const res = await api({ query, variables })
			this.isDeleteLoading = false

			if (res?.data?.deleteLogin?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.deleteLogin?.errors ||
							res?.data?.deleteLogin?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			}
			this.$router.push({ name: "UsersManagement" })
			this.$store.dispatch("showSuccess", this.$t("user_deleted"))
		}
	},

	computed: {
		loginId() {
			return this.$route.params.loginId
		},

		computedAssociatedUser() {
			const createdAt = dayjs(this.associatedUser?.createdAt).format(
				this.$store.state.settings.dateFormat
			)
			const type = this.associatedUser?.__typename

			const defaultFields = [
				{
					label: this.$t("type"),
					value: type
				},
				{
					label: this.$t("created_at"),
					value: createdAt
				},
				{
					label: this.$t("name"),
					value: this.associatedUser?.name
				}
			]

			if (type === "company") return defaultFields

			if (type === "internal" || type === "individual")
				return [
					...defaultFields,
					{
						label: this.$t("english_first_name"),
						value: this.associatedUser?.englishFirstName
					},
					{
						label: this.$t("english_last_name"),
						value: this.associatedUser?.englishLastName
					},
					{
						label: this.$t("chinese_first_name"),
						value: this.associatedUser?.chineseFirstName
					},
					{
						label: this.$t("chinese_last_name"),
						value: this.associatedUser?.chineseLastName
					}
				]
			else return []
		},

		computedPermissionGroup() {
			return [
				{
					label: this.$t("role"),
					value:
						this.permissionGroups?.length > 0
							? this.permissionGroups?.[0]?.name
							: "No permission group selected",
					hasUpdate: true
				}
			]
		}
	},

	mounted() {
		this.fetchUser()
	}
}
</script>
